"use client"

import { useState } from "react"
import Link from "next/link"
import { Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const t = useTranslations("auth.forgotPassword")
  const tCommon = useTranslations("common")

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      setSubmitted(true)
    } catch {
      toast.error(tCommon("genericError"))
    } finally {
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
          {t("checkEmailTitle")}
        </h1>
        <p className="mt-3 text-[13px] text-muted-foreground">
          {t("checkEmailBody", { email })}
        </p>
        <Link
          href="/login"
          className="mt-7 inline-flex h-12 w-full items-center justify-center rounded-lg border border-border bg-background text-sm font-semibold text-foreground transition-colors hover:border-[var(--color-border-strong)]"
        >
          {t("back")}
        </Link>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
        {t("title")}
      </h1>
      <p className="mt-1 text-[13px] text-muted-foreground">
        {t("subtitle")}
      </p>

      <form onSubmit={handleSubmit} className="mt-7 space-y-5">
        <div className="space-y-2">
          <label htmlFor="email" className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("emailLabel")}
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            className="h-12 w-full rounded-lg border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all"
            required
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-3 flex h-12 w-full cursor-pointer items-center justify-center rounded-lg bg-blue-600 text-sm font-semibold text-white shadow-[0_0_20px_rgba(59,130,246,0.25)] transition-all hover:bg-blue-600/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("submitting")}
            </>
          ) : (
            t("submit")
          )}
        </button>

        <Link
          href="/login"
          className="block text-center text-[13px] text-muted-foreground hover:text-foreground"
        >
          {t("back")}
        </Link>
      </form>
    </div>
  )
}
